import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/contexts/CartContext';
import { useProducts } from '@/contexts/ProductsContext';
import { useToast } from '@/contexts/ToastContext';
import { fetchApprovedReviews } from '@/lib/productReviewsApi';
import { fetchPublicStock, stockForSlug, type StockBySku } from '@/lib/publicStock';
import { trackAddToCart, trackViewContent } from '@/lib/tracking';
import ProductV2 from '@/shared/rxk-product/ProductV2';
import { RxkProductHost } from '@/pages/products/RxkProductHost';
import type { ProductReview } from '@/types/product';

// New-design 3-Step Treatment page: the set shown with the product page
// layout plus its own sections (ProductV2 set mode). Content is the set's
// rxk_product_content row (SET-3STEP), edited in Admin > Products.
export default function SetPageV2() {
  const { t } = useTranslation();
  const { setProduct, loading } = useProducts();
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const [stock, setStock] = useState<StockBySku>({});
  const [reviews, setReviews] = useState<ProductReview[]>([]);

  useEffect(() => {
    fetchPublicStock().then(setStock);
  }, []);
  useEffect(() => {
    if (!setProduct) return;
    fetchApprovedReviews(setProduct.sku).then(setReviews);
    trackViewContent({ id: setProduct.id, name: setProduct.name, price: Number(setProduct.price.replace(/[^0-9.]/g, '')) });
  }, [setProduct]);

  if (!setProduct) return <div className="w-full min-h-screen bg-white" aria-busy={loading} />;

  const stockQty = stockForSlug(setProduct.id, stock);
  const outOfStock = stockQty !== null && stockQty <= 0;
  const add = (qty = 1) => {
    if (outOfStock) return;
    addToCart(setProduct.id, qty);
    trackAddToCart({ id: setProduct.id, name: setProduct.name, price: Number(setProduct.price.replace(/[^0-9.]/g, '')), quantity: qty });
    showToast(t('cart.added', 'Added to Cart ✓'), t('cart.viewCart', 'View Cart →'), openCart);
  };

  return (
    <RxkProductHost>
      <ProductV2 product={setProduct} outOfStock={outOfStock} reviews={reviews} onAddToCart={add} />
    </RxkProductHost>
  );
}
